<template>
    <div class="d-flex flex-row">
        <div class="fiche_status" :class="statusClass">
            <p :class="statusTextClass">
                <v-icon v-if="statusIcon" :name="statusIcon" />
                {{ statusMessage }}
            </p>
        </div>
    </div>
</template>

<script>
export default {
    name: 'StatutCoureur',

    props: {
        runner: {
            type: Object,
            required: true
        },

    },
    computed: {
        /**
         * Texte du statut du coureur
         */
        statutText() {
            return (this.runner && this.runner.statut && this.runner.statut.text) || '';
        },

        /**
         * Le coureur est-il en course ?
         */
        isEnCourse() {
            return this.statutText.includes('En course');
        },

        /**
         * Le coureur est-il arrivé ?
         */
        isArrive() {
            return this.statutText.includes('Arrivé');
        },

        /**
         * Le coureur a-t-il abandonné ou été disqualifié ?
         */
        isAbandon() {
            return this.statutText.includes('Abandon') || this.statutText.includes('Disqualifié');
        },

        /**
         * Classes CSS dynamiques pour le statut
         */
        statusClass() {
            return {
                'fiche_status--running': this.isEnCourse,
                'fiche_status--finished': !this.isEnCourse
            };
        },

        /**
         * Classe CSS pour le style du texte selon le statut
         */
        statusTextClass() {
            if (this.isArrive) return 'status_arrive';
            if (this.isAbandon) return 'status_abandon';
            return 'status_autre';
        },

        /**
         * Icône à afficher selon le statut
         */
        statusIcon() {
            if (this.isEnCourse) return null;
            if (this.isArrive) return 'check';
            if (this.isAbandon) return 'times';
            return 'ellipsis-h';
        },

        /**
         * Calcule le timestamp de départ du coureur
         * @returns {number|null} Timestamp en millisecondes ou null
         */
        timestampDepart() {
            try {
                const statutCourse = this.runner && this.runner.statut_course;
                if (!statutCourse || !statutCourse.date || !statutCourse.heure_depart) {
                    return null;
                }

                const [jour, mois, annee] = statutCourse.date.split("-");
                const [heures, minutes, secondes] = statutCourse.heure_depart.split(":");

                if (!jour || !mois || !annee || !heures || !minutes || !secondes) {
                    return null;
                }

                const dateDepart = new Date(
                    parseInt(annee),
                    parseInt(mois) - 1,
                    parseInt(jour),
                    parseInt(heures),
                    parseInt(minutes),
                    parseInt(secondes)
                );

                return dateDepart.getTime();
            } catch (error) {
                console.error('Erreur lors du calcul du timestamp de départ:', error);
                return null;
            }
        },

        /**
         * Calcule le temps de course en secondes
         * Utilise le currentTime du store pour la réactivité
         * @returns {number|null} Temps de course en secondes ou null
         */
        tempsCourseSecondes() {
            if (!this.isEnCourse || !this.timestampDepart || !this.$store.state.live_en_cours) {
                return null;
            }

            // Utilise currentTime du store pour la réactivité
            const now = this.$store.state.currentTime;
            const tempsEcoule = Math.floor((now - this.timestampDepart) / 1000);

            return tempsEcoule >= 0 ? tempsEcoule : null;
        },

        /**
         * Formate le temps de course au format HH:MM:SS
         * @returns {string} Temps formaté
         */
        temps_de_course() {
            if (!this.tempsCourseSecondes) {
                return '';
            }



            let totalSeconds = this.tempsCourseSecondes;
            const timeNeutralise = this.runner.estimation && this.runner.estimation.timeBetweenNeutraliseSection;
            if (timeNeutralise && timeNeutralise > 0) {
                totalSeconds -= timeNeutralise;
            }
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            const pad = (num) => num.toString().padStart(2, '0');

            return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        },

        /**
         * Message du statut à afficher
         */
        statusMessage() {
            if (this.isEnCourse && this.temps_de_course) {
                return `En course : ${this.temps_de_course}`;
            }
            return this.statutText;
        }
    },

    mounted() {

        // Démarre l'interval global s'il n'est pas déjà démarré
        this.$store.commit('startTimeInterval');
    },

    beforeUnmount() {
        // Note: On ne stoppe pas l'interval ici car d'autres composants peuvent l'utiliser
        // L'interval sera nettoyé quand l'application se ferme ou quand le live se termine
    }
}
</script>

<style lang="scss" scoped>
.fiche_status {
    &--finished {
        margin-bottom: 10px;
    }

    p {
        margin: 0;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }
}
</style>